import { ChevronDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import TaxSummaryAmountInput from "@/components/despacho/TaxSummaryAmountInput";
import { formatTaxSummaryAmount } from "@/lib/taxSummaryWorkbookConstants";

function formatBracketMoney(value) {
  const formatted = formatTaxSummaryAmount(value, "money");
  return formatted || "—";
}

export default function TaxSummaryResicoPfPanel({
  meta = {},
  resicoBrackets = [],
  readOnly = false,
  onMetaChange,
  defaultOpen = false,
}) {
  const usarTasaFija = meta.resico_usar_tasa_fija === true
    || String(meta.resico_usar_tasa_fija).toLowerCase() === "true"
    || meta.resico_modo_tasa === "fija";

  const patchMeta = (patch) => {
    if (readOnly || !onMetaChange) return;
    onMetaChange({ ...meta, ...patch });
  };

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="rounded-sm border border-slate-200 bg-slate-50/60"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-100/80">
        <div>
          <p className="text-xs font-semibold text-slate-800 m-0">
            Tasa RESICO — persona física (régimen de confianza)
          </p>
          <p className="text-[10px] text-slate-500 m-0 mt-0.5">
            Tabla SAT por ingresos cobrados del mes. El ISR causado se precalcula al guardar o recalcular.
          </p>
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition-transform [[data-state=open]_&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-200 data-[state=closed]:hidden">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="radio"
              name="resico_pf_modo"
              disabled={readOnly}
              checked={!usarTasaFija}
              onChange={() => patchMeta({
                resico_modo_tasa: "tramos",
                resico_usar_tasa_fija: false,
              })}
            />
            Tabla por tramos (automático)
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="radio"
              name="resico_pf_modo"
              disabled={readOnly}
              checked={usarTasaFija}
              onChange={() => patchMeta({
                resico_modo_tasa: "fija",
                resico_usar_tasa_fija: true,
              })}
            />
            Tasa fija personalizada
          </label>
          {usarTasaFija ? (
            <div className="space-y-1 min-w-[120px]">
              <Label className="text-[10px] text-slate-600">Tasa RESICO</Label>
              <TaxSummaryAmountInput
                className="text-right w-28"
                value={meta.tasa_resico || ""}
                editable={!readOnly}
                amountMode="coeficiente"
                variant="field"
                placeholder="0.01"
                onChange={(next) => patchMeta({ tasa_resico: next })}
              />
            </div>
          ) : null}
        </div>

        {resicoBrackets?.length > 0 && !usarTasaFija ? (
          <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Ingreso cobrado del mes — desde</TableHead>
                  <TableHead className="text-xs">Hasta</TableHead>
                  <TableHead className="text-xs">Tasa ISR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resicoBrackets.map((b, i) => (
                  <TableRow key={`rb-pf-${i}`}>
                    <TableCell className="text-xs tabular-nums">{formatBracketMoney(b.desde)}</TableCell>
                    <TableCell className="text-xs tabular-nums">
                      {b.hasta == null ? "En adelante" : formatBracketMoney(b.hasta)}
                    </TableCell>
                    <TableCell className="text-xs">{b.tasa_pct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}

        <p className="text-[10px] text-slate-500 m-0">
          El impuesto sugerido (ISR causado) usa los ingresos cobrados del mes y la tasa del tramo correspondiente.
          Puedes forzar una tasa fija si el SAT publica un ajuste o para escenarios de prueba.
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}
